import { createClient } from "@supabase/supabase-js";
import type { PortalConfig } from "../lib/config.js";
import { activationCallbackError, consumeActivationCallback, type ActivationAuthClient, type ActivationCallback, type ActivationInvalidReason } from "./activation.js";

export const RECOVERY_PATH = "/reset-password";
export const RECOVERY_STORAGE_KEY = "ohf-password-recovery";

export type RecoveryResult =
  | { kind: "ready"; identity: { id: string; email: string } }
  | { kind: "invalid"; reason: "missing" | "malformed" | "expired" | "unavailable" };

export function createRecoveryClient(config: PortalConfig): ActivationAuthClient | null {
  if (config.authMode !== "jwt" || !config.supabaseUrl || !config.supabasePublishableKey) return null;
  return createClient(config.supabaseUrl, config.supabasePublishableKey, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false, storageKey: RECOVERY_STORAGE_KEY },
  }) as unknown as ActivationAuthClient;
}

async function initializeRecovery(callback: ActivationCallback, client: ActivationAuthClient): Promise<RecoveryResult> {
  if (callback.type !== "recovery") return { kind: "invalid", reason: "missing" };
  try {
    const exchanged = callback.kind === "session"
      ? await client.auth.setSession({ access_token: callback.accessToken, refresh_token: callback.refreshToken })
      : await client.auth.verifyOtp({ token_hash: callback.tokenHash, type: "recovery" });
    if (exchanged.error || !exchanged.data.session?.access_token) return { kind: "invalid", reason: "expired" };
    const { data, error } = await client.auth.getUser();
    const user = data.user;
    if (error || !user?.id || !user.email || !user.email_confirmed_at || user.is_anonymous) {
      return { kind: "invalid", reason: "expired" };
    }
    // Credential recovery does not create/activate an OHF directory row or grant
    // staff access. Pending applicants can recover and then sign in normally.
    return { kind: "ready", identity: { id: user.id, email: user.email } };
  } catch {
    return { kind: "invalid", reason: "unavailable" };
  }
}

/** One isolated attempt survives React StrictMode, never a page reload. */
export class PasswordRecoveryCoordinator {
  private callback: ActivationCallback | null = null;
  private captured = false;
  private client: ActivationAuthClient | null | undefined;
  private initialization: Promise<RecoveryResult> | null = null;
  private leases = 0;
  private releaseTimer: ReturnType<typeof setTimeout> | null = null;
  private generation = 0;
  private invalidReason: ActivationInvalidReason = "missing";

  capture(url: URL, scrub: () => void): void {
    if (url.pathname !== RECOVERY_PATH) return;
    if (this.captured && !url.search && !url.hash) return;
    this.clear();
    this.invalidReason = activationCallbackError(url);
    const callback = consumeActivationCallback(url, scrub);
    this.callback = callback?.type === "recovery" ? callback : null;
    if (callback && callback.type !== "recovery") this.invalidReason = "malformed";
    this.captured = true;
  }

  retain(): () => void {
    this.leases += 1;
    if (this.releaseTimer) clearTimeout(this.releaseTimer);
    this.releaseTimer = null;
    return () => {
      this.leases = Math.max(0, this.leases - 1);
      if (!this.leases) this.releaseTimer = setTimeout(() => { if (!this.leases) this.clear(); }, 0);
    };
  }

  acquireClient(factory: () => ActivationAuthClient | null): ActivationAuthClient | null {
    if (this.client === undefined) this.client = factory();
    return this.client;
  }

  landingState(): { kind: "confirmation" } | { kind: "loading" } | Extract<RecoveryResult, { kind: "invalid" }> {
    return this.initialization ? { kind: "loading" } : this.callback ? { kind: "confirmation" } : { kind: "invalid", reason: this.invalidReason === "expired" ? "expired" : this.invalidReason === "missing" ? "missing" : "malformed" };
  }

  started(): Promise<RecoveryResult> | null { return this.initialization; }

  initialize(client: ActivationAuthClient): Promise<RecoveryResult> {
    if (this.initialization) return this.initialization;
    if (!this.callback) return Promise.resolve({ kind: "invalid", reason: this.invalidReason === "expired" ? "expired" : "missing" });
    const callback = this.callback;
    const generation = this.generation;
    this.callback = null;
    this.initialization = initializeRecovery(callback, client).then(async result => {
      if (generation !== this.generation || result.kind === "invalid") {
        await client.auth.signOut({ scope: "local" }).catch(() => undefined);
      }
      return generation === this.generation ? result : { kind: "invalid", reason: "expired" };
    });
    return this.initialization;
  }

  clear(): void {
    this.generation += 1;
    if (this.client) void this.client.auth.signOut({ scope: "local" }).catch(() => undefined);
    this.client = undefined;
    this.callback = null;
    this.initialization = null;
    this.captured = false;
    if (this.releaseTimer) clearTimeout(this.releaseTimer);
    this.releaseTimer = null;
  }
}

export const passwordRecoveryCoordinator = new PasswordRecoveryCoordinator();

let nextRecoveryRequestAt = 0;
export async function requestPasswordRecovery(
  client: { auth: { resetPasswordForEmail(email: string, options: { redirectTo: string }): Promise<{ error: unknown }> } },
  email: string,
  origin: string,
  now = Date.now(),
): Promise<"acknowledged" | "wait" | "unavailable"> {
  if (now < nextRecoveryRequestAt) return "wait";
  // UI pacing supplements Supabase's enforced Auth rate limits.
  nextRecoveryRequestAt = now + 60_000;
  try {
    const result = await client.auth.resetPasswordForEmail(email.trim(), { redirectTo: new URL(RECOVERY_PATH, origin).href });
    return result.error ? "unavailable" : "acknowledged";
  } catch {
    return "unavailable";
  }
}
